import { conceptClcModules } from './conceptClcCourseData'

export interface LessonSection {
  id: string
  title: string
  emoji?: string
  number?: string
  content: string
  image?: string
  imageAlt?: string
  quote?: string
  contractUrl?: string
  type?:
    | 'text'
    | 'video'
    | 'conclusion'
    | 'records'
    | 'performance-grid'
    | 'performance-calculator'
    | 'career-table'
    | 'career-timeline'
    | 'career-calculator'
    | 'tenant-qualification-script'
    | 'qualification-info-table'
    | 'crm-timeline'
    | 'qualification-benefits'
    | 'image-gallery'
    | 'contract-preview'
  records?: Array<{
    icon: string
    category: string
    record: string
    observation: string
  }>
  careerLevels?: Array<{
    level: string
    rentals: string
    sales: string
    description: string
    color: string
  }>
  timelineSteps?: Array<{
    from: string
    to: string
    requirement: string
    description: string
  }>
}
export interface Lesson {
  id: string
  title: string
  duration: string
  description: string
  content?: string
  sections?: LessonSection[]
}
export interface Module {
  id: string
  title: string
  icon: string
  description: string
  lessons: Lesson[]
}
export interface Course {
  id: string
  title: string
  description: string
  status: 'active' | 'locked'
  progress?: number
  lessons?: Lesson[]
  modules: Module[]
}

// Flat array of all modules (legacy export for backward compatibility)
export const courseData: Module[] = [
  {
    id: 'client-proprietar',
    title: 'Modul 1 — Client Proprietar',
    icon: 'home',
    description:
      'Strategii de prospectare și gestionare a relației cu proprietarii',
    lessons: [
      {
        id: 'comisionare-proprietar',
        title: 'Comisionare Proprietar',
        duration: '16 min',
        description:
          'Structura de comisioane pentru colaborarea cu proprietarii',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În închiriere, comisionarea este unul dintre cele mai sensibile subiecte pentru proprietar.\n\nNu pentru că suma ar fi mare, ci pentru că proprietarul nu vede încă rezultatul.\n\nRolul agentului NU este să justifice comisionul, ci să explice logica economică și lipsa riscului.\n\n👉 Proprietarul nu plătește un serviciu.\n👉 Proprietarul plătește un rezultat.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Agent imobiliar explicând contract proprietarului',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• cum funcționează comisionarea proprietarului la închiriere\n• când se plătește comisionul (și când NU)\n• cum explici clar valoarea comisionului\n• de ce comisionarea este corectă economic\n• cum răspunzi calm la obiecții legate de bani\n• ce diferență este între închiriere și vânzare din punct de vedere financiar',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție comisionare',
            type: 'text',
          },
          {
            id: 'principiu-baza',
            title: 'Principiul de bază al comisionării',
            emoji: '🤝',
            number: '1️⃣',
            content:
              'Comisionarea proprietarului la închiriere se bazează pe un principiu simplu:\n\n👉 Cost 0 pe muncă. Comision doar pe rezultat.\n\nAsta înseamnă:\n\n• proprietarul nu plătește nimic în avans\n• nu există taxe de promovare\n• nu există costuri ascunse\n• dacă nu se închiriază → nu se plătește nimic\n\n📌 Agenția își asumă munca și riscul.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Principiu colaborare',
            quote: 'Cost 0 pe muncă. Comision doar pe rezultat.',
            type: 'text',
          },
          {
            id: 'comision-standard',
            title: 'Cât este comisionul standard',
            emoji: '💰',
            number: '2️⃣',
            content:
              '👉 Comision standard proprietar: 50% din chiria unei luni + TVA\n\nExemplu:\n\n• chirie: 600 €\n• comision: 300 € + TVA\n\n📌 Important:\n\n• există posibilitatea de derogare\n• comisionul poate deveni 50% TVA inclus, doar cu aprobare din partea agenției\n\n👉 Agentul NU promite derogări din start.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Comision standard',
            type: 'text',
          },
          {
            id: 'cand-se-plateste',
            title: 'Când se plătește comisionul',
            emoji: '💶',
            number: '3️⃣',
            content:
              'Comisionul NU se plătește:\n\n• la semnarea contractului de intermediere\n• la publicarea anunțului\n• la prima vizionare\n• dacă chiriașul nu este acceptat\n\n👉 Comisionul se plătește DOAR DUPĂ:\n\n• semnarea contractului de închiriere Proprietar–Chiriaș\n• încasarea chiriei de către proprietar\n\n📌 Proprietarul plătește doar când are banii în buzunar.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Momentul plății comisionului',
            quote: 'Proprietarul plătește doar când are banii în buzunar.',
            type: 'text',
          },
          {
            id: 'diferenta-inchiriere-vanzare',
            title: 'Diferența esențială între închiriere și vânzare',
            emoji: '📊',
            number: '4️⃣',
            content:
              'Un proprietar de închiriere trebuie să înțeleagă un lucru-cheie:\n\n📉 Un imobil neînchiriat produce pierdere lunară.\n\nSpre deosebire de vânzare:\n\n• la vânzare → prețul rămâne, banii vin o singură dată\n• la închiriere → fiecare lună liberă = bani pierduți\n\n👉 Mai bine:\n\n• plătești o singură dată un comision\n• pui rapid imobilul în producție financiară',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Diferență închiriere vs vânzare',
            type: 'text',
          },
          {
            id: 'de-ce-justificat',
            title: 'De ce comisionul este justificat',
            emoji: '✅',
            number: '5️⃣',
            content:
              'Prin colaborarea cu agenția, proprietarul primește:\n\n• chiriași calificați\n• timp economisit\n• vizionări organizate\n• filtrarea cererilor\n• asistență până la semnarea contractului\n• suport dacă apar probleme\n\n👉 Comisionul NU este pentru „un anunț".\n👉 Este pentru rezolvarea unei probleme.',
            image:
              'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=600&fit=crop',
            imageAlt: 'Beneficii colaborare',
            quote:
              'Comisionul NU este pentru „un anunț". Este pentru rezolvarea unei probleme.',
            type: 'text',
          },
          {
            id: 'situatie-speciala',
            title: 'Situație specială: plecarea chiriașului',
            emoji: '🔄',
            number: '6️⃣',
            content:
              'Un avantaj important care trebuie comunicat clar:\n\n👉 Dacă chiriașul pleacă, agenția poate găsi alt chiriaș\n👉 fără comisionare suplimentară, cu o condiție:\n\n📌 proprietarul oferă exclusivitate agenției pentru reluarea închirierii\n\nAceastă variantă:\n\n• protejează proprietarul\n• creează continuitate\n• elimină costuri repetate',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Continuitate închiriere',
            type: 'text',
          },
          {
            id: 'cum-se-stabileste',
            title: 'Cum se stabilește comisionul',
            emoji: '💼',
            number: '7️⃣',
            content:
              'Formula principală pentru calcularea comisionului este simplă și directă:\n\n💼 Comision Agenție = 50% × Chirie Lunară + TVA (21%)\n\nTVA-ul se aplică la valoarea comisionului și se include în factura emisă după semnarea contractului. Acest lucru înseamnă că valoarea finală pe care o primește agentia include atât comisionul de bază, cât și taxa pe valoarea adăugată.',
            type: 'commission-table',
          },
          {
            id: 'calculator',
            title: 'Calculator interactiv de comision',
            emoji: '🧮',
            number: '7️⃣.1',
            content:
              'Folosește calculatorul de mai jos pentru a determina rapid comisionul pentru orice valoare a chiriei lunare.',
            type: 'commission-calculator',
          },
          {
            id: 'ce-nu-faci',
            title: 'Ce NU faci ca agent',
            emoji: '❌',
            number: '8️⃣',
            content:
              '❌ Nu negociezi comisionul din prima\n❌ Nu intri în defensivă\n❌ Nu te justifici excesiv\n❌ Nu compari cu „alte agenții mai ieftine"\n\n👉 Schimbi perspectiva, nu procentul.',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Ce nu faci ca agent',
            type: 'text',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Comisionarea proprietarului trebuie explicată:\n\n• calm\n• logic\n• fără presiune\n\nReține:\n\n• proprietarul nu are risc\n• agenția își asumă munca\n• plata se face doar la rezultat\n• timpul costă bani în închiriere\n\n💬 Un agent profesionist nu vinde un comision. Un agent profesionist explică o soluție.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în comisionare',
            quote:
              'Un agent profesionist nu vinde un comision. Un agent profesionist explică o soluție.',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'oferta-agentie',
        title: 'Oferta Agenție',
        duration: '18 min',
        description: 'Prezentarea valorii și serviciilor oferite de agenție',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În prospectarea pentru închiriere, nu apelul este problema, ci cum este prezentată oferta.\n\nMajoritatea proprietarilor:\n\n• vor să închirieze singuri\n• sunt reticenți față de agenții\n• au avut experiențe neplăcute în trecut\n\nRolul agentului nu este să convingă cu forța, ci să explice clar oferta și să arate valoarea reală a colaborării.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Agent prezentând oferta profesional',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• care este oferta agenției către proprietarii de închiriere\n• cum răspunde oferta la întrebarea „de ce o agenție imobiliară"\n• ce face Cautlocuinta.ro diferit\n• cum funcționează comisionarea și când se plătește\n• cum să explici lipsa exclusivității\n• cum să poziționezi agenția ca alternativă, nu ca obligație',
            image:
              'https://cdn.magicpatterns.com/uploads/vnfeJRvPJsHHs1aE4XNcfj/image.png',
            imageAlt: 'Agent explicând oferta',
            type: 'text',
          },
          {
            id: 'oferta-catre-proprietar',
            title: 'Care este oferta agenției către proprietar',
            emoji: '🎁',
            number: '1️⃣',
            content:
              'Oferta Cautlocuinta.ro este construită pe un principiu simplu:\n\n👉 **cost 0 pe muncă, comision doar pe rezultat**\n\nAsta înseamnă:\n\n• proprietarul nu plătește nimic în avans\n• nu există costuri ascunse\n• agenția este plătită doar dacă apare un chiriaș potrivit și se închiriază imobilul\n\n**Proprietarul:**\n\n• păstrează controlul\n• decide dacă acceptă sau nu chiriașul\n• nu este obligat să colaboreze exclusiv',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Oferta agenției',
            type: 'text',
          },
          {
            id: 'de-ce-agentie',
            title: 'De ce o agenție imobiliară',
            emoji: '🤔',
            number: '2️⃣',
            content:
              'Un proprietar poate închiria și singur, dar:\n\n• pierde timp cu apeluri inutile\n• face vizionări cu chiriași nepotriviți\n• își asumă riscuri mai mari\n\n**Agenția aduce:**\n\n• chiriași filtrați\n• economie de timp\n• un proces organizat\n• suport până la finalizarea închirierii\n\n👉 **Agentul nu înlocuiește proprietarul, îl ajută.**',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Beneficiile agenției',
            quote: 'Agentul nu înlocuiește proprietarul, îl ajută.',
            type: 'text',
          },
          {
            id: 'de-ce-cautlocuinta',
            title: 'De ce Cautlocuinta.ro',
            emoji: '🏢',
            number: '3️⃣',
            content:
              'Cautlocuinta.ro NU promite miracole, ci oferă:\n\n• acces la o bază activă de chiriași\n• filtrarea cererilor înainte de vizionare\n• colaborare fără exclusivitate\n• comision doar din rezultat\n\n**Diferența este:**\n\n• risc asumat de agenție\n• flexibilitate pentru proprietar\n• transparență în proces',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Diferențierea Cautlocuinta.ro',
            type: 'text',
          },
          {
            id: 'comisionare',
            title: 'Comisionare: cât și când se plătește',
            emoji: '💰',
            number: '4️⃣',
            content:
              'Comisionul:\n\n• este un procent din chiria pe prima lună\n• se plătește doar dacă se închiriază imobilul\n• se achită după ce proprietarul încasează chiria\n\n📌 **Important:**\n\n• nu se plătește nimic la semnarea contractului de intermediere\n• nu se plătește nimic dacă nu apare un rezultat',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Comisionare transparentă',
            type: 'text',
          },
          {
            id: 'lipsa-exclusivitatii',
            title: 'Lipsa exclusivității',
            emoji: '🔓',
            number: '5️⃣',
            content:
              'Colaborarea NU este exclusivă.\n\n**Proprietarul poate:**\n\n• colabora și cu alte agenții\n• încerca să închirieze și pe cont propriu\n\n**Pentru agenție, lipsa exclusivității înseamnă:**\n\n• muncă fără garanții\n• comision doar dacă apare rezultatul\n\n👉 **Riscul este la agenție, nu la proprietar.**',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Colaborare flexibilă',
            quote: 'Riscul este la agenție, nu la proprietar.',
            type: 'text',
          },
          {
            id: 'contract-regula',
            title: 'Contractul și regula vizionării',
            emoji: '📄',
            number: '6️⃣',
            content:
              'Contractul de intermediere:\n\n• se semnează doar dacă există interes real\n• de regulă, la prima vizionare cu un chiriaș\n\n**Regulă clară:**\n\n❌ fără contract → ❌ fără vizionare\n\nDacă proprietarul sau chiriașul refuză semnarea:\n\n➡️ vizionarea se anulează\n\n**Această regulă:**\n\n• protejează munca agentului\n• evită situații neclare\n• menține profesionalismul procesului',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract și reguli clare',
            type: 'text',
          },
          {
            id: 'filtrare-chirasi',
            title: 'Filtrarea chiriașilor și diminuarea riscurilor',
            emoji: '✅',
            number: '7️⃣',
            content:
              'Un beneficiu major al colaborării este filtrarea chiriașilor.\n\n**Agentul:**\n\n• califică cererea\n• verifică bugetul și timing-ul\n• reduce riscul de neplată sau probleme ulterioare\n\n**Proprietarul:**\n\n• vede mai puțini chiriași\n• dar mai bine potriviți',
            image:
              'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&h=600&fit=crop',
            imageAlt: 'Verificare și filtrare chiriași',
            type: 'text',
          },
          {
            id: 'agentie-alternativa',
            title: 'Agenția ca alternativă la închirierea pe cont propriu',
            emoji: '🔄',
            number: '8️⃣',
            content:
              'Colaborarea cu agenția nu înseamnă renunțarea la închirierea pe cont propriu.\n\n**Înseamnă:**\n\n• o sursă suplimentară de chiriași\n• un canal în plus\n• șanse mai mari într-un timp mai scurt\n\n📉 **În închiriere, timpul este critic:**\n\n• cu cât un imobil stă mai mult liber\n• cu atât randamentul anual scade\n• iar prețul de închiriere este ajustat în jos',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Timp și oportunitate',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește prezentarea video',
            content:
              'În acest video, avatarul AI îți explică pas cu pas cum să prezinți oferta agenției într-un mod clar, simplu și fără presiune.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Oferta agenției trebuie prezentată:\n\n• clar\n• simplu\n• fără presiune\n\n**Reține:**\n\n• proprietarul nu cumpără un serviciu\n• proprietarul acceptă o colaborare\n• agenția își asumă riscul\n\n**O ofertă bine explicată:**\n\n• reduce obiecțiile\n• crește șansele de colaborare\n• face prospectarea mult mai ușoară\n\n💬 În lecția următoare, vei învăța cum să gestionezi obiecțiile proprietarilor și să transformi refuzurile în colaborări.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Prezentare profesională',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'obiectii-colaborare',
        title: 'Obiecții Colaborare',
        duration: '22 min',
        description:
          'Gestionarea obiecțiilor și transformarea refuzurilor în colaborări',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În închiriere, proprietarul nu caută agenți. Caută să nu piardă bani, să închirieze repede și să evite problemele.\n\nMajoritatea obiecțiilor NU sunt împotriva agenției, ci împotriva:\n\n• riscului\n• costului\n• pierderii controlului\n\n👉 Rolul agentului este să explice calm că:\n\n• agenția este o alternativă\n• nu există risc\n• decizia finală este 100% la proprietar',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Conversație profesională',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• să tratezi obiecțiile proprietarilor de închiriere fără defensivă\n• să explici logic de ce agenția nu este un cost, ci o investiție\n• să muți discuția din „preț" în „pierdere de bani în timp"\n• să poziționezi Cautlocuinta.ro ca furnizor suplimentar de chiriași',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'regula-baza',
            title: 'Regula de bază',
            emoji: '📌',
            number: '1️⃣',
            content:
              '📌 Nu contrazici proprietarul.\n📌 Nu îi spui că greșește.\n📌 Nu negociezi din prima.\n\n👉 Tu explici.\n👉 El decide.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Regula de bază',
            type: 'text',
          },
          {
            id: 'obiectie-de-ce-agentie',
            title: 'Obiecția: "De ce să colaborez cu o agenție?"',
            emoji: '❓',
            number: '2️⃣',
            content:
              '💬 **Răspuns recomandat (detaliat, explicativ):**\n\n"Agenția este doar o alternativă în plus, pe lângă ce faceți deja.\n\nDacă apare un chiriaș potrivit și îl acceptați, perfect. Dacă nu apare sau nu vă place, nu aveți nimic de plătit.\n\nNoi vă ajutăm să înțelegeți că agenția nu este un cost, ci o investiție ca imobilul să înceapă cât se poate de repede să producă bani. De asta îl închiriați și nu îl vindeți – urmăriți o sursă de venit pasiv.\n\nDacă prin noi reușiți să îl închiriați mai repede, practic v-am pus imobilul în producție financiară. Lăsați-mă să vă bag banii în buzunar."\n\n👉 Repoziționezi agenția: opțiune economică, nu obligație.',
            image:
              'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=600&fit=crop',
            imageAlt: 'De ce agenție',
            type: 'text',
          },
          {
            id: 'obiectie-comision-mare',
            title: 'Obiecția: "Comisionul este prea mare"',
            emoji: '💰',
            number: '3️⃣',
            content:
              '💬 **Răspuns recomandat (dezvoltat exact pe logica ta):**\n\n"La închiriere nu vorbim de un cost permanent, ci de unul singur, o singură dată.\n\nDacă imobilul stă neînchiriat:\n\n• pierdeți chiria lunară\n• pierdeți randament anual\n• pierdeți timp\n\nDacă, prin agenție, îl puneți mai repede în producție financiară, costul intermedierii se amortizează foarte repede.\n\nÎn plus, este foarte important pe cine alegeți ca și chiriaș. Ca alegerea să fie făcută cum trebuie, aveți nevoie de:\n\n• mai multe variante\n• chiriași filtrați\n• oameni care pot susține financiar un contract pe termen mai lung\n\nIar dacă chiriașul pleacă înainte de expirarea contractului, vă ajutăm să aveți continuitate, găsind altul fără comision, cu condiția exclusivității."\n\n👉 Schimbi discuția din „cât plătesc" în „cât pierd dacă aștept".',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Discuție despre comision',
            type: 'text',
          },
          {
            id: 'obiectie-singur',
            title: 'Obiecția: "Pot să închiriez singur"',
            emoji: '🤔',
            number: '4️⃣',
            content:
              '💬 **Răspuns recomandat:**\n\n"Sigur că puteți și este normal să încercați.\n\nColaborarea cu agenția nu vă oprește din asta. Noi doar adăugăm încă o sursă de chiriași, în paralel."\n\n📌 Proprietarul nu renunță la nimic.\n👉 Doar câștigă o opțiune în plus.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Pot să închiriez singur',
            type: 'text',
          },
          {
            id: 'obiectie-alta-agentie',
            title: 'Obiecția: "Altă agenție cere comision mai mic"',
            emoji: '🏢',
            number: '5️⃣',
            content:
              '🔹 **Varianta 1 – Explicație completă (principală)**\n\n💬 **Răspuns recomandat (păstrând formularea ta):**\n\n"Degeaba aveți o colaborare cu alte agenții pe comision mai mic dacă imobilul încă nu este închiriat. Asta înseamnă că, până acum, agenția respectivă nu și-a făcut treaba.\n\nChiar dacă agenția cu comision mai mic vă găsește chiriașul potrivit, nu este nicio problemă să îl acceptați – este și normal, au un cost mai mic.\n\nDar lăsați-mă să fiu și eu un furnizor de chiriași pentru dumneavoastră. Pentru că degeaba vă găsește agenția cealaltă un chiriaș dacă este nepotrivit:\n\n• nu are grijă de imobil\n• nu este potent financiar\n• nu își asumă un contract pe durată mai lungă\n\nNu comisionarea e cea mai importantă ci mai mult Chiriașul pe care il băgați in imobil. Un chiriaș bun, o afacere buna si liniștită. Cu mai multe variante bune, aveți control și luați o decizie corectă. Haideți sa fim serioși, nu aveți nimic de pierdut, de ce credeți ca ne numim Cautlocuință.ro, avem clienți ne trebuie doar colaborari cu proprietari"\n\n👉 Mesajul-cheie: mai multe surse = chiriaș mai bun, nu conflict.\n\n🔹 **Varianta 2 – Concesie maximă (doar dacă insistă)**\n\n💬 **Continuare (exact cum ai cerut):**\n\n"Ce pot face maxim pentru dumneavoastră este să vorbesc cu agenția pentru o derogare de introducere a TVA-ului în comisionare.\n\nAstfel, în loc de 50% + TVA, putem avea 👉 50% comision inclus TVA.\n\nPractic, vorbim de un discount de 21%, ceea ce este enorm, pentru că TVA-ul oricum îl plătim noi către stat."\n\n📌 Este maximum posibil, nu negociere liberă.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Altă agenție mai ieftină',
            type: 'text',
          },
          {
            id: 'obiectie-tva',
            title: 'Obiecția: "Nu vreau să plătesc TVA"',
            emoji: '💶',
            number: '6️⃣',
            content:
              '🔹 **Varianta 1 – Normalizare (umană)**\n\n💬 **Răspuns recomandat (păstrând tonul tău):**\n\n"Dar cine vrea să plătească TVA?\n\nDin păcate, nu plătiți TVA doar aici la intermediere. Plătiți TVA:\n\n• când cumpărați o pâine\n• când băgați benzină\n• când luați o haină\n\nDiferența este că acolo produsul este definit ca preț final și nu știți cât este TVA-ul. Aici, fiind vorba de servicii, se calculează procent + TVA."\n\n🔹 **Varianta 2 – Soluție concretă (la insistență)**\n\n💬 **Continuare:**\n\n"Ce pot face maxim pentru dumneavoastră este să vorbesc cu agenția pentru o derogare, astfel încât să avem 👉 50% comision inclus TVA.\n\nPractic, este un discount de 21%, ceea ce este foarte mult, pentru că TVA-ul nu dispare, doar îl absorbim noi."',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Obiecție TVA',
            type: 'text',
          },
          {
            id: 'obiectie-contract',
            title: 'Obiecția: "Nu vreau să semnez nimic"',
            emoji: '📄',
            number: '7️⃣',
            content:
              '💬 **Răspuns recomandat:**\n\n"În acest moment nu este nimic de semnat.\n\nContractul apare doar dacă există un chiriaș interesat și ajungem la vizionare. Nu semnați ca să încercați, ci doar ca să fie clar că, dacă acceptați chiriașul nostru, colaborarea este corectă pentru ambele părți."\n\n📌 Fără contract → fără vizionare.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract și claritate',
            type: 'text',
          },
          {
            id: 'obiectie-timp',
            title: 'Obiecția: "Nu am timp / nu vreau vizionări inutile"',
            emoji: '⏰',
            number: '8️⃣',
            content:
              '💬 **Răspuns recomandat:**\n\n"Tocmai de asta filtrăm chiriașii înainte.\n\nNu venim cu oameni care doar se uită. Venim cu chiriași care:\n\n• se încadrează în buget\n• au intenții clare\n• pot lua o decizie rapidă."',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Economie de timp',
            type: 'text',
          },
          {
            id: 'beneficiu-continuitate',
            title: 'Beneficiu suplimentar – Continuitatea închirierii',
            emoji: '🔄',
            number: '9️⃣',
            content:
              '👉 Dacă chiriașul pleacă, vă găsim alt chiriaș fără comision, cu condiția să ne oferiți exclusivitate pentru reluarea închirierii.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Continuitate închiriere',
            type: 'text',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '🔟',
            content:
              '**În închiriere:**\n\n• timpul este dușmanul\n• imobilul gol înseamnă bani pierduți\n• colaborarea cu agenția este o investiție punctuală, nu un cost recurent\n\n💬 Un agent bun nu se apără. Un agent bun explică și lasă proprietarul să decidă.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în gestionarea obiecțiilor',
            quote:
              'Un agent bun nu se apără. Un agent bun explică și lasă proprietarul să decidă.',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'apel-prospectare-inchiriere',
        title: 'Apel prospectare închiriere',
        duration: '24 min',
        description:
          'Tehnici de comunicare telefonică pentru prospectarea proprietăților',
        sections: [
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei înțelege:\n\n• Cum se pregătește corect un apel de prospectare către un proprietar\n• Ce informații trebuie analizate înainte de apel\n• Care este obiectivul principal al apelului (colaborarea și acordul de intermediere)\n• Cum se folosește scriptul oficial al agenției pentru obținerea colaborării',
            image:
              'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&h=600&fit=crop',
            imageAlt: 'Agent imobiliar discutând la telefon cu proprietar',
            type: 'text',
          },
          {
            id: 'ce-este-apelul',
            title: 'Ce este apelul de prospectare',
            emoji: '🧭',
            number: '1️⃣',
            content:
              'Apelul de prospectare este primul contact profesional pe care un agent îl are cu un proprietar care a publicat un anunț de închiriere.\n\nScopul acestui apel este de a:\n\n• Obține colaborarea proprietarului\n• Explica clar condițiile comisionării (50% + TVA 21%)\n• Stabili o relație de încredere și profesionalism\n\n💡 Gândește-te la acest apel ca la o primă impresie. În primele 30 de secunde, proprietarul își formează o opinie despre tine și agenția noastră.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Agent pregătindu-se pentru apel',
            type: 'text',
          },
          {
            id: 'pregatirea-apelului',
            title: 'Pregătirea apelului',
            emoji: '🔹',
            number: '2️⃣',
            content:
              'Înainte de a efectua apelul, trebuie să parcurgi următorii pași esențiali:',
            type: 'call-preparation-checklist',
          },
          {
            id: 'obiectivul-apelului',
            title: 'Obiectivul apelului',
            emoji: '🧾',
            number: '3️⃣',
            content:
              'Obiectivul principal este obținerea acordului de colaborare și explicarea comisionului agenției.\n\nAgentul trebuie să transmită:\n\n• Agenția colaborează doar cu proprietari care acceptă intermedierea\n• Comisionul standard este 50% + TVA (21%)\n• Acesta se plătește doar după semnarea contractului\n\n💡 Nu vinzi serviciul — vinzi soluția la o nevoie reală: găsirea chiriașului potrivit.',
            image:
              'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&h=600&fit=crop',
            imageAlt: 'Agent explicând beneficiile colaborării',
            type: 'text',
          },
          {
            id: 'scriptul-oficial',
            title: 'Scriptul oficial complet CLC',
            emoji: '📞',
            number: '4️⃣',
            content:
              'Scriptul oficial al agenției este structurat pentru a crea o conversație naturală și profesională cu proprietarul. Urmează exact acest script pentru a obține cele mai bune rezultate.',
            type: 'call-script-accordion',
          },
          {
            id: 'reguli-importante',
            title: 'Reguli importante în apel',
            emoji: '🎯',
            number: '5️⃣',
            content:
              'Respectă următoarele reguli pentru a asigura succesul apelului:',
            type: 'call-rules-grid',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            content:
              'În acest video, avatarul AI îți explică pas cu pas cum să conduci un apel de prospectare eficient și profesional.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🌱',
            number: '6️⃣',
            content:
              'Apelul de prospectare este primul pas către o colaborare de încredere. Este momentul în care demonstrezi profesionalism, claritate și respect pentru timpul proprietarului.\n\nAplică exact structura din această lecție, folosește scriptul oficial și vei vedea cum rata de conversie crește constant.\n\n💬 Ține minte: nu suni ca să vinzi, ci ca să construiesti o colaborare.\n\nÎn următoarea lecție, vei învăța cum să gestionezi listele de prospectare și cum să organizezi activitatea zilnică pentru rezultate maxime.',
            image:
              'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=600&fit=crop',
            imageAlt: 'Agent discutând cu proprietar la telefon',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'liste-prospectare',
        title: 'Liste prospectare',
        duration: '18 min',
        description: 'Crearea și gestionarea listelor de prospectare',
        sections: [
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei înțelege:\n\n• Cum funcționează listele zilnice de prospectare închiriere\n• Ce conțin și cum se prelucrează corect\n• Care sunt regulile și sancțiunile aplicabile\n• Cum se verifică în CRM și se finalizează un lead',
            image:
              'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1200&h=600&fit=crop',
            imageAlt: 'Agent analizând fișier digital cu grafic și checklist',
            type: 'text',
          },
          {
            id: 'ce-sunt-listele',
            title: 'Ce sunt listele de prospectare închiriere',
            emoji: '🧭',
            number: '1️⃣',
            content:
              'Listele de prospectare sunt instrumentul zilnic de lucru al fiecărui agent din agenția CautLocuinta.ro. Ele conțin seturi de proprietari reali, colectați și verificați zilnic de echipa agenției din principalele surse online (Imobiliare.ro, OLX, Storia, Romimo, AnuntulTelefon etc.).\n\nFiecare agent primește lista sa personalizată în format Google Sheet, cu date actualizate zilnic.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Mockup listă Google Sheet pe laptop',
            quote:
              'Lista ta zilnică = motorul activității tale. Fiecare linie este o oportunitate.',
            type: 'text',
          },
          {
            id: 'continut-lista',
            title: 'Ce conține lista',
            emoji: '📋',
            number: '2️⃣',
            content:
              'Fiecare listă de prospectare conține informații esențiale despre proprietăți și proprietari, organizate în coloane clare și ușor de procesat.',
            type: 'prospecting-list-table',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Fragment de listă colorată CautLocuinta',
          },
          {
            id: 'alocare-prelucrare',
            title: 'Alocare și prelucrare zilnică',
            emoji: '🔹',
            number: '3️⃣',
            content:
              'Listele sunt distribuite zilnic, între 09:00–10:00, și trebuie prelucrate integral până la finalul zilei.\n\n✔️ Prelucrare completă înseamnă:\n\n• Fiecare proprietar verificat în CRM\n• Fiecare lead apelat conform scriptului oficial\n• Fiecare linie completată cu status\n• Toate colaborările aprobate au ID CRM generat\n\n⏱️ Respectarea termenului zilnic este obligatorie. Nerespectarea atrage blocarea generării unei liste noi.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Calendar digital cu ceas',
            type: 'text',
          },
          {
            id: 'procedura-lucru',
            title: 'Procedura de lucru – Pas cu pas',
            emoji: '⚙️',
            number: '4️⃣',
            content:
              'Urmează acest proces zilnic pentru fiecare lead din listă:',
            type: 'prospecting-workflow',
          },
          {
            id: 'statusuri',
            title: 'Statusuri disponibile după apel',
            emoji: '🧩',
            number: '5️⃣',
            content:
              'După fiecare apel, trebuie să completezi statusul corect în listă. Iată toate statusurile disponibile și semnificația lor:',
            type: 'status-table',
          },
          {
            id: 'monitorizare',
            title: 'Monitorizare și conversie',
            emoji: '📊',
            number: '6️⃣',
            content:
              'Agenția monitorizează zilnic performanța ta prin:\n\n• Gradul de prelucrare (% leaduri lucrate vs. total)\n• Rata de conversie (colaborări / total leaduri)\n• Calitatea completării în listă și CRM\n\n📈 Conversia minimă acceptată: 40% colaborări valide din totalul listelor procesate.\n\n⚠️ Neîndeplinirea constantă a pragului sau lipsa prelucrării atrage:\n\n• Blocarea contului\n• Audit de performanță\n• Închiderea colaborării',
            image:
              'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
            imageAlt: 'Dashboard cu KPI-uri',
            type: 'text',
          },
          {
            id: 'consecinte',
            title: 'Consecințe pentru nerespectarea regulilor',
            emoji: '⚠️',
            number: '7️⃣',
            content:
              'Sistemul de liste funcționează pe bază de disciplină și responsabilitate. Iată consecințele pentru nerespectarea regulilor:',
            type: 'consequences-table',
          },
          {
            id: 'video',
            title: 'Urmărește prezentarea video',
            content:
              'În acest video, avatarul AI îți explică pas cu pas cum funcționează listele de prospectare, cum se completează corect și care sunt cele mai bune practici.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🌱',
            number: '8️⃣',
            content:
              'Listele de prospectare sunt motorul zilnic al veniturilor tale. Ele îți oferă leaduri reale, filtrate și gata de contact. Misiunea ta este să le lucrezi cu disciplină, atenție și perseverență.\n\n💬 "Un agent care lucrează lista zilnic nu-și face griji pentru următoarea tranzacție."\n\nÎn lecția următoare, vei învăța cum să gestionezi clienții chiriași și cum să creezi anunțuri atractive pentru proprietățile din portofoliu.',
            image:
              'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&h=600&fit=crop',
            imageAlt: 'Agent verificând progresul în dashboard CRM',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
  {
    id: 'client-chirias',
    title: 'Modul 2 — Client Chiriaș',
    icon: 'user-check',
    description:
      'Gestionarea relației cu clienții chiriași și procesul de calificare',
    lessons: [
      {
        id: 'comisionare-chirias',
        title: 'Comisionare Chiriaș',
        duration: '14 min',
        description: 'Structura de comisioane pentru clienții chiriași',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'Cele mai multe conflicte cu chiriașii NU apar din cauza comisionului, ci din cauza momentului greșit în care este comunicat.\n\n👉 Regula CLC este simplă: comisionul se spune din timp, clar și fără ocoliri.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Comunicare clară cu chiriașul',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• când se comunică comisionul către chiriaș\n• cât este comisionul standard\n• ce reguli speciale se aplică chiriașilor care sună pe proprietăți din portofoliul agenției\n• când și cum se plătește comisionul\n• cum previi obiecțiile înainte să apară',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție comisionare',
            type: 'text',
          },
          {
            id: 'ce-este-comisionul',
            title: 'Ce este comisionul de la chiriaș',
            emoji: '💼',
            number: '1️⃣',
            content:
              'Comisionul de la chiriaș reprezintă:\n\n• plata serviciilor de intermediere\n• accesul la proprietăți reale, verificate\n• suport până la semnarea contractului de închiriere\n\n📌 Chiriașul NU plătește pentru o vizionare, ci pentru rezultatul final: închirierea.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Servicii de intermediere',
            type: 'text',
          },
          {
            id: 'comision-standard',
            title: 'Cât este comisionul standard',
            emoji: '💰',
            number: '2️⃣',
            content:
              '🔹 **Comision standard chiriaș: 50% din valoarea unei luni de chirie + TVA**\n\n**Caracteristici:**\n\n• se plătește o singură dată\n• se plătește DOAR dacă se închiriază\n• NU există costuri în avans',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Comision standard',
            type: 'text',
          },
          {
            id: 'regula-speciala',
            title:
              'Regula specială – chiriașii care sună pe proprietăți din portofoliul agenției',
            emoji: '📌',
            number: '3️⃣',
            content:
              '**Regulă obligatorie CLC**\n\nPentru chiriașii care:\n\n• sună direct pe un anunț din portofoliul agenției\n• solicită informații despre o proprietate listată de Cautlocuinta.ro\n\n👉 Comisionul standard de 50% + TVA se comunică IMEDIAT, după furnizarea informațiilor de interes despre proprietate.\n\n**Ordinea corectă a discuției:**\n\n1. Oferi detaliile relevante despre proprietate\n2. Clarifici interesul chiriașului\n3. Comunici clar:\n\n"Pentru această proprietate, comisionul agenției este standard, 50% din prima lună de chirie + TVA, plătibil doar dacă se închiriază."\n\n📌 Fără amânări. Fără „vedem mai târziu".',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Comunicare imediată comision',
            quote: 'Fără amânări. Fără „vedem mai târziu".',
            type: 'text',
          },
          {
            id: 'de-ce-din-timp',
            title: 'De ce comisionul se spune din timp',
            emoji: '⏰',
            number: '4️⃣',
            content:
              'Comunicarea timpurie:\n\n• filtrează chiriașii neserioși\n• evită refuzurile la vizionare\n• creează transparență\n• protejează timpul agentului\n\n👉 Un chiriaș informat din start este un chiriaș mai cooperant.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Transparență și comunicare',
            type: 'text',
          },
          {
            id: 'cand-se-plateste',
            title: 'Când se plătește comisionul',
            emoji: '💶',
            number: '5️⃣',
            content:
              'Comisionul se plătește:\n\n✅ DUPĂ semnarea contractului de închiriere P–C\n✅ DOAR dacă chiriașul închiriază imobilul\n\n❌ NU:\n• la apel\n• la vizionare\n• la rezervare',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Momentul plății comisionului',
            type: 'text',
          },
          {
            id: 'contract-intermediere',
            title: 'Contractul de intermediere cu chiriașul',
            emoji: '📄',
            number: '6️⃣',
            content:
              'Pentru orice vizionare:\n\n• este necesar contract de intermediere cu chiriașul\n• se semnează înainte de intrarea în imobil\n• poate fi semnat fizic sau digital\n\n❌ Fără contract → ❌ fără vizionare\n\nAceasta nu este o opțiune, ci o regulă de lucru.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract de intermediere',
            type: 'text',
          },
          {
            id: 'obiectii-frecvente',
            title: 'Obiecții frecvente ale chiriașului',
            emoji: '💬',
            number: '7️⃣',
            content:
              '**"De ce trebuie să plătesc comision?"**\n\n"Pentru că vă asist pe tot procesul și plătiți doar dacă se închiriază. Dacă nu ajungem la rezultat, nu aveți nimic de plătit."\n\n**"Mi se pare mult"**\n\n"Este o plată unică și vine doar la rezultat. Vă economisește timp și evita situații neclare."\n\n**"Pot găsi și singur"**\n\n"Sigur, este alegerea dumneavoastră. Rolul meu este să vă ajut mai rapid și mai organizat."\n\n👉 Ton calm. Fără justificări excesive.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Gestionarea obiecțiilor',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '8️⃣',
            content:
              '❌ Amânarea comunicării comisionului\n❌ Vizionări fără contract\n❌ Negocierea comisionului fără aprobare\n❌ Presupunerea că „nu contează acum"',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Greșeli de evitat',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți explică pas cu pas cum să comunici corect comisionul către chiriași.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Comisionarea chiriașului:\n\n• trebuie spusă din timp\n• trebuie spusă clar\n• trebuie spusă la fel, de fiecare dată\n\n**Reține:**\n\n• transparența elimină conflictele\n• regulile clare cresc conversia\n• comisionul este parte din proces\n\n💬 Un agent bun evită surprizele. Un agent profesionist creează claritate de la primul apel.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în comisionare',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'calificare-chirias',
        title: 'Calificarea Chiriașului',
        duration: '26 min',
        description:
          'Procesul de preluare și evaluare a potențialilor chiriași',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În închiriere, apelul chiriașului este punctul zero al procesului.\n\nIndiferent dacă chiriașul sună:\n\n• pentru o proprietate clar identificată\n• sau doar „după ce a văzut un anunț"\n\n➡️ calificarea este obligatorie de fiecare dată.\n\nUn agent nu este call-center, un agent este filtru între cerere și ofertă.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Agent profesionist la telefon',
            quote:
              'Un agent nu este call-center, un agent este filtru între cerere și ofertă.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• cum începe corect recepția unui apel de la chiriaș\n• cum identifici rapid proprietatea pentru care a sunat\n• ce faci dacă nu poate oferi ID-ul proprietății\n• care sunt întrebările-cheie de calificare\n• când un chiriaș este eligibil pentru lucrat\n• cum se indexează corect cererea în CRM',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'receptia-apelului',
            title: 'Recepția apelului – identificarea proprietății',
            emoji: '📞',
            number: '1️⃣',
            content:
              '**🔹 Pasul 1: Solicitarea ID-ului de proprietate**\n\nPrimul lucru pe care îl faci este identificarea proprietății.\n\n**Exemplu de formulare:**\n\n"Pentru a vă ajuta corect, vă rog să-mi spuneți ID-ul proprietății. Este un cod care începe cu litera P, de exemplu P9829, și se găsește la începutul descrierii anunțului."\n\n📌 Acesta este standardul CLC.\n\n**🔹 Pasul 2: Dacă chiriașul NU are ID-ul**\n\nDacă nu mai are anunțul în față, NU închizi apelul și NU sari peste proces.\n\nCeri detalii clare pentru identificare:\n\n• tip imobil (garsonieră / 2 / 3 camere etc.)\n• zona\n• preț aproximativ\n• alte repere relevante\n\n**Exemplu:**\n\n"În regulă, spuneți-mi atunci ce tip de imobil era, zona și prețul, ca să identificăm despre ce proprietate este vorba."\n\n📌 Indiferent dacă proprietatea este identificată sau nu, urmează calificarea chiriașului.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Identificare proprietate',
            type: 'text',
          },
          {
            id: 'regula-de-aur',
            title: 'Regula de aur: calificarea NU se sare niciodată',
            emoji: '⚠️',
            number: '2️⃣',
            content:
              'Chiar dacă:\n\n• proprietatea este clar identificată\n• chiriașul pare hotărât\n• cere direct vizionare\n\n➡️ **calificarea se face de fiecare dată.**',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Regulă obligatorie',
            type: 'text',
          },
          {
            id: 'intrebari-esentiale',
            title: 'Întrebările esențiale de calificare (OBLIGATORIU)',
            emoji: '❓',
            number: '3️⃣',
            content:
              'Aceste întrebări NU sunt opționale:\n\n**🔹 Cine va locui în imobil?**\n\n• o singură persoană\n• cuplu\n• familie\n• colegi\n\n**🔹 Câte persoane vor locui efectiv?**\n\n• adulți\n• copii\n\n**🔹 Perioada de închiriere dorită**\n\n• minim 12 luni\n• perioadă mai scurtă\n• nedeterminată\n\n📌 Dacă aceste răspunsuri nu sunt clare, chiriașul nu este calificat.',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Întrebări esențiale',
            type: 'text',
          },
          {
            id: 'criterii-importante',
            title: 'Alte criterii importante de calificare',
            emoji: '📋',
            number: '4️⃣',
            content:
              'În funcție de caz, verifici:\n\n• buget maxim real\n• termen de mutare\n• animale de companie\n• disponibilitate pentru vizionare\n• flexibilitate pe zonă sau tip imobil\n\n📌 Cerințele trebuie să fie coerente cu piața, nu ideale.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Criterii calificare',
            type: 'text',
          },
          {
            id: 'comunicare-comision',
            title: 'Comunicarea comisionului către chiriaș',
            emoji: '💰',
            number: '5️⃣',
            content:
              'Pentru proprietățile din portofoliul agenției:\n\n👉 **Comision standard: 50% + TVA**\n\nAceastă informație:\n\n• se comunică clar\n• se comunică în apel\n• fără justificări inutile\n\n**Exemplu:**\n\n"Vă informez de la început că pentru această proprietate comisionul agenției este de 50% + TVA, plătit o singură dată, doar dacă se închiriază."\n\n📌 **Dacă nu acceptă → procesul se oprește aici**\n📌 **Dacă acceptă → continuă procesul**',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Comunicare comision',
            type: 'text',
          },
          {
            id: 'decizia-agentului',
            title: 'Decizia agentului: lucrăm sau nu chiriașul',
            emoji: '✅',
            number: '6️⃣',
            content:
              '**✔️ Chiriaș eligibil dacă:**\n\n• cererea este clară\n• bugetul este realist\n• acceptă comisionul\n• perioada este coerentă\n\n**❌ Chiriaș neeligibil dacă:**\n\n• evită răspunsurile\n• schimbă constant cerințele\n• vrea excepții din start\n• refuză comisionul\n\n📌 Agentul decide, nu chiriașul.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Decizie agent',
            quote: 'Agentul decide, nu chiriașul.',
            type: 'text',
          },
          {
            id: 'indexare-crm',
            title: 'Indexarea în CRM (OBLIGATORIU)',
            emoji: '💼',
            number: '7️⃣',
            content:
              'Indiferent de rezultat:\n\n• cererea se indexează în CRM\n• se notează:\n  - proprietatea (ID P sau descriere)\n  - calificat / necalificat\n  - buget\n  - cerințe\n  - observații\n\n📌 Chiriașii neeligibili NU se lucrează, dar rămân indexați pentru viitor.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Indexare CRM',
            type: 'text',
          },
          {
            id: 'pasi-urmatori',
            title: 'Legătura cu pașii următori',
            emoji: '🔗',
            number: '8️⃣',
            content:
              'Doar chiriașii calificați:\n\n• intră în matching intern\n• intră în matching extern\n• pot ajunge la vizionări\n\n📌 Fără calificare → fără vizionare',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Pași următori',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să recepționezi corect un apel de la chiriaș și să faci calificarea profesionist.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Calificarea chiriașului:\n\n• salvează timp\n• reduce vizionările inutile\n• crește rata de închidere\n\n📌 **Un agent slab aleargă după toți**\n📌 **Un agent bun alege pe cine lucrează**\n\nRecepția apelului făcută corect:\n\n• controlează procesul\n• protejează agentul\n• menține calitatea portofoliului',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în calificare profesională',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
  {
    id: 'matching',
    title: 'Modul 3 — Matching',
    icon: 'check-circle',
    description: 'Procesul de potrivire între proprietăți și chiriași',
    lessons: [
      {
        id: 'matching-chirias-intern',
        title: 'Matching Chiriaș Intern',
        duration: '22 min',
        description:
          'Potrivirea chiriașilor cu proprietățile din portofoliul intern',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'Matching-ul intern este primul și cel mai important pas după calificarea chiriașului.\n\nÎnainte să cauți în piață:\n➡️ verifici ce există deja în portofoliul agenției.\n\nDe cele mai multe ori:\n\n• proprietatea potrivită există deja\n• este în portofoliul tău sau al unui coleg\n• iar tranzacția se poate închide rapid\n\nMatching-ul intern înseamnă:\n👉 eficiență, viteză și control.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Matching intern proprietăți',
            quote: 'Matching-ul intern înseamnă: eficiență, viteză și control.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• ce înseamnă matching intern\n• când se face matching-ul intern\n• cum verifici portofoliul tău și al colegilor\n• cum contactezi corect un coleg\n• ce verificări sunt obligatorii înainte de a propune o proprietate\n• când oprești matching-ul intern și treci mai departe',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție matching',
            type: 'text',
          },
          {
            id: 'ce-este-matching',
            title: 'Ce este Matching-ul Intern',
            emoji: '🔍',
            number: '1️⃣',
            content:
              'Matching intern = potrivirea unui chiriaș calificat cu:\n\n• proprietățile tale\n• proprietățile colegilor din agenție\n\n📌 Se lucrează exclusiv cu cereri calificate.\n📌 Fără calificare → nu există matching.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Proces matching intern',
            type: 'text',
          },
          {
            id: 'cand-se-face',
            title: 'Când se face Matching-ul Intern',
            emoji: '⏰',
            number: '2️⃣',
            content:
              'Matching-ul intern se face:\n\n• imediat după calificarea chiriașului\n• înainte de orice căutare externă\n• înainte de a discuta vizionări\n\n📌 Este primul filtru din proces.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Timing matching',
            type: 'text',
          },
          {
            id: 'portofoliu-propriu',
            title: 'Matching în portofoliul propriu',
            emoji: '📁',
            number: '3️⃣',
            content:
              'Primul pas este verificarea:\n\n• proprietăților tale active\n• statusului acestora (disponibil / rezervat / închiriat)\n• compatibilității reale cu cererea chiriașului\n\n**⚠️ Nu propui:**\n\n• proprietăți „aproape potrivite"\n• proprietăți cu diferențe majore de buget\n• proprietăți care nu respectă cerințele de bază',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Verificare portofoliu propriu',
            type: 'text',
          },
          {
            id: 'portofoliu-colegi',
            title: 'Matching în portofoliul colegilor din agenție',
            emoji: '👥',
            number: '4️⃣',
            content:
              'Dacă nu ai proprietate potrivită în portofoliul tău:\n➡️ verifici portofoliul intern al agenției.\n\n**🔹 Regula obligatorie**\n\nDacă identifici o proprietate a unui coleg:\n👉 îl contactezi înainte de orice propunere către chiriaș.\n\n**Exemplu:**\n\n"Salut, am un chiriaș calificat pentru proprietatea ta Pxxxx. Mai este disponibilă și corespunde cererii?"',
            image:
              'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&h=600&fit=crop',
            imageAlt: 'Colaborare între colegi',
            type: 'text',
          },
          {
            id: 'verificari-obligatorii',
            title: 'Verificări obligatorii cu colegul',
            emoji: '✅',
            number: '5️⃣',
            content:
              'Înainte să propui chiriașului:\n\n• confirmi că proprietatea este disponibilă\n• verifici dacă există vizionări deja programate\n• confirmi condițiile reale (preț, termen, cerințe proprietar)\n\n📌 Niciodată nu promiți vizionare fără confirmare.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Verificări obligatorii',
            quote: 'Niciodată nu promiți vizionare fără confirmare.',
            type: 'text',
          },
          {
            id: 'prezentare-proprietate',
            title: 'Prezentarea proprietății către chiriaș',
            emoji: '🏠',
            number: '6️⃣',
            content:
              'După confirmare:\n\n• prezinți proprietatea clar și corect\n• NU exagerezi\n• NU omiți informații relevante\n\n📌 Scopul este:\n👉 interes real, nu doar vizionare.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Prezentare proprietate',
            type: 'text',
          },
          {
            id: 'decizia-continuare',
            title: 'Decizia: mergem mai departe sau nu',
            emoji: '🎯',
            number: '7️⃣',
            content:
              'Dacă chiriașul:\n\n• este interesat\n• acceptă condițiile\n• este disponibil\n\n➡️ se trece la programarea vizionării, conform regulilor:\n\n• contracte semnate înainte de vizionare\n• indexare în CRM\n\n**Dacă nu:**\n➡️ continui matching-ul intern sau treci la matching extern.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Decizie continuare',
            type: 'text',
          },
          {
            id: 'cand-se-opreste',
            title: 'Când se oprește Matching-ul Intern',
            emoji: '🛑',
            number: '8️⃣',
            content:
              'Matching-ul intern se oprește când:\n\n• nu există proprietăți compatibile\n• sau cele existente nu mai sunt disponibile\n\nAbia atunci:\n➡️ se trece la Matching Chiriaș – Extern.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Oprire matching intern',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să faci matching intern eficient și cum să colaborezi corect cu colegii.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Matching-ul intern:\n\n• scurtează timpul până la închiriere\n• crește rata de succes\n• reduce efortul inutil\n\n📌 **Un agent bun:**\n\n• verifică întâi intern\n• comunică cu colegii\n• nu promite fără confirmare\n\n📌 **Matching-ul intern este baza colaborării în agenție.**',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes matching intern',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'matching-extern',
        title: 'Matching Extern',
        duration: '20 min',
        description: 'Colaborarea cu alte agenții pentru matching extern',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'Matching-ul extern începe doar după ce matching-ul intern a fost epuizat.\n\nScopul nu este să „cauți la întâmplare", ci să completezi cererea unui chiriaș calificat atunci când:\n\n• nu există proprietăți potrivite în agenție\n• sau cele existente nu mai sunt disponibile\n\nMatching-ul extern înseamnă:\n👉 extinderea căutării în piață, controlat și organizat.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Matching extern piață',
            quote:
              'Matching-ul extern înseamnă: extinderea căutării în piață, controlat și organizat.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• când se face matching extern\n• unde se caută proprietăți externe\n• cum verifici o proprietate din afara agenției\n• cum contactezi corect alți agenți sau proprietari\n• ce reguli trebuie respectate înainte de a propune chiriașului',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție matching extern',
            type: 'text',
          },
          {
            id: 'cand-se-face',
            title: 'Când se face Matching-ul Extern',
            emoji: '⏰',
            number: '1️⃣',
            content:
              'Matching-ul extern se face doar dacă:\n\n• matching-ul intern nu a generat variante potrivite\n• cererea chiriașului este validă și coerentă\n• chiriașul este activ și pregătit de vizionare\n\n📌 Nu sari peste matching-ul intern.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Când matching extern',
            type: 'text',
          },
          {
            id: 'unde-se-cauta',
            title: 'Unde se face Matching-ul Extern',
            emoji: '🌐',
            number: '2️⃣',
            content:
              'Căutarea externă se face în aceleași locuri unde caută chiriașii:\n\n• storia.ro\n• olx.ro\n• imobiliare.ro\n• imoradar24.ro\n• publi24.ro\n• romimo.ro\n\n📌 Aceste platforme sunt instrumente de lucru, nu doar surse de anunțuri.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Platforme online',
            type: 'text',
          },
          {
            id: 'identificare-proprietati',
            title: 'Identificarea proprietăților potrivite',
            emoji: '🔍',
            number: '3️⃣',
            content:
              'Când găsești o proprietate:\n\n• verifici compatibilitatea cu cererea chiriașului\n• verifici prețul, zona, condițiile\n• verifici dacă anunțul este actual\n\n**⚠️ Nu propui:**\n\n• proprietăți „la limită"\n• proprietăți fără date clare\n• proprietăți neconfirmate',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Identificare proprietăți',
            type: 'text',
          },
          {
            id: 'contactare-agenti',
            title: 'Contactarea agenților sau proprietarilor',
            emoji: '📞',
            number: '4️⃣',
            content:
              'Înainte de a propune chiriașului:\n➡️ trebuie să confirmi disponibilitatea.\n\n**La contact:**\n\n• te prezinți clar ca agent\n• explici că ai un chiriaș calificat\n• întrebi explicit dacă proprietatea este disponibilă\n\n📌 Fără confirmare → fără propunere.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Contact profesional',
            quote: 'Fără confirmare → fără propunere.',
            type: 'text',
          },
          {
            id: 'verificari-obligatorii',
            title: 'Verificări obligatorii înainte de propunere',
            emoji: '✅',
            number: '5️⃣',
            content:
              'Înainte să mergi mai departe, confirmi:\n\n• disponibilitatea proprietății\n• condițiile reale (preț, garanție, termen)\n• dacă se acceptă colaborare între agenții\n\n📌 Dacă nu există deschidere:\n➡️ nu pierzi timp, continui căutarea.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Verificări obligatorii',
            type: 'text',
          },
          {
            id: 'propunere-chirias',
            title: 'Propunerea către chiriaș',
            emoji: '🏠',
            number: '6️⃣',
            content:
              'După confirmare:\n\n• prezinți proprietatea chiriașului\n• explici clar condițiile\n• menționezi comisionul (dacă este cazul)\n\n📌 Scopul nu este vizionarea, ci identificarea interesului real.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Propunere către chiriaș',
            type: 'text',
          },
          {
            id: 'programare-vizionare',
            title: 'Programarea vizionării',
            emoji: '📅',
            number: '7️⃣',
            content:
              'Dacă chiriașul este interesat:\n\n• verifici condițiile de vizionare\n• clarifici semnarea contractelor de intermediere\n• programezi vizionarea doar după confirmări\n\n📌 **Regulile rămân aceleași:**\n\n• fără contract → fără vizionare\n• fără indexare → proces incomplet',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Programare vizionare',
            type: 'text',
          },
          {
            id: 'indexare-crm',
            title: 'Indexarea în CRM',
            emoji: '💼',
            number: '8️⃣',
            content:
              'Orice matching extern:\n\n• se notează în CRM\n• se marchează sursa (extern)\n• se actualizează statusul cererii\n\n📌 CRM-ul este memorie, nu opțional.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Indexare CRM',
            quote: 'CRM-ul este memorie, nu opțional.',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să faci matching extern eficient și cum să colaborezi cu alți agenți sau proprietari.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Matching-ul extern:\n\n• completează portofoliul agenției\n• crește șansele de închiriere\n• menține chiriașii activi\n\n📌 **Un agent eficient:**\n\n• caută structurat\n• confirmă înainte de a promite\n• respectă procesul\n\n📌 **Matching-ul extern nu înlocuiește matching-ul intern, ci îl continuă logic.**',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes matching extern',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
  {
    id: 'contracte',
    title: 'Modul 4 — Contracte',
    icon: 'file-text',
    description: 'Întocmirea și gestionarea contractelor de închiriere',
    lessons: [
      {
        id: 'contract-proprietar',
        title: 'Contract Proprietar',
        duration: '28 min',
        description: 'Elaborarea și semnarea contractului cu proprietarul',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În închiriere, munca agentului începe înainte de vizionare, dar colaborarea reală începe doar după contract.\n\n**Contractul cu proprietarul:**\n\n• NU este o formalitate\n• NU este un risc pentru proprietar\n• ESTE documentul care clarifică regulile jocului\n\n👉 Fără contract semnat, nu există vizionare.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract și semnătură',
            quote: 'Fără contract semnat, nu există vizionare.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• ce este contractul de intermediere cu proprietarul\n• de ce este obligatoriu\n• când se semnează\n• ce reguli impune\n• cum explici contractul simplu, fără tensiuni\n• ce se întâmplă dacă una dintre părți refuză semnarea',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție contract',
            type: 'text',
          },
          {
            id: 'ce-este-contractul',
            title: 'Ce este Contractul Proprietar',
            emoji: '📄',
            number: '1️⃣',
            content:
              'Contractul de intermediere cu proprietarul este:\n\n• acordul de colaborare dintre agenție și proprietar\n• documentul care stabilește:\n  - comisionarea\n  - lipsa exclusivității\n  - momentul plății\n  - regulile vizionărilor\n\n📌 Contractul NU obligă proprietarul să închirieze.\n📌 Contractul NU generează costuri fără rezultat.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Contract de intermediere',
            type: 'text',
          },
          {
            id: 'principiul-baza',
            title: 'Principiul de bază al colaborării',
            emoji: '🤝',
            number: '2️⃣',
            content:
              'Contractul Cautlocuinta.ro este construit pe un principiu clar:\n\n👉 **Cost 0 pe muncă, comision doar pe rezultat**\n\nAsta înseamnă:\n\n• proprietarul nu plătește nimic în avans\n• agenția își asumă riscul\n• comisionul apare doar dacă:\n  - proprietarul acceptă chiriașul\n  - se semnează contractul de închiriere\n  - proprietarul încasează chiria',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Principiu colaborare',
            quote: 'Cost 0 pe muncă, comision doar pe rezultat',
            type: 'text',
          },
          {
            id: 'cand-se-semneaza',
            title: 'Când se semnează Contractul Proprietar',
            emoji: '✍️',
            number: '3️⃣',
            content:
              'Contractul se semnează:\n\n• înainte de prima vizionare\n• fizic sau digital\n\n📌 **Regula este fermă:**\n\n❌ fără contract semnat → ❌ fără vizionare\n\n**Dacă:**\n\n• proprietarul refuză semnarea\n• sau amână fără motiv clar\n\n➡️ vizionarea se anulează.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Semnare contract',
            type: 'text',
          },
          {
            id: 'de-ce-obligatoriu',
            title: 'De ce contractul este obligatoriu înainte de vizionare',
            emoji: '🛡️',
            number: '4️⃣',
            content:
              'Contractul:\n\n• protejează munca agentului\n• elimină situațiile neclare\n• creează un cadru profesionist\n• evită „vizionări la liber"\n\n👉 **Fără contract:**\n\n• agentul muncește fără protecție\n• proprietarul nu are reguli clare\n• apar conflicte inutile',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Protecție contract',
            type: 'text',
          },
          {
            id: 'ce-contine',
            title: 'Ce conține, pe scurt, contractul',
            emoji: '📋',
            number: '5️⃣',
            content:
              'Contractul stabilește clar:\n\n• părțile implicate\n• imobilul (adresă exactă)\n• comisionul agenției\n• momentul plății\n• lipsa exclusivității\n• durata colaborării\n\n📌 Datele trebuie completate corect și complet, fără aproximări.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Conținut contract',
            type: 'text',
          },
          {
            id: 'cum-explici',
            title: 'Cum explici contractul proprietarului (uman, simplu)',
            emoji: '💬',
            number: '6️⃣',
            content:
              '**Exemplu de explicație:**\n\n"Contractul nu vă obligă să închiriezi cu noi. Este doar documentul prin care stabilim clar că:\n\n– nu aveți costuri pe muncă\n– comisionul apare doar dacă acceptați chiriașul\n– iar noi putem lucra organizat și profesionist."\n\n📌 Fără presiune. Fără limbaj juridic.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Explicație contract',
            type: 'text',
          },
          {
            id: 'previzualizare-contract',
            title: 'Previzualizare Contract Proprietar',
            emoji: '📄',
            number: '7️⃣',
            content:
              'Pentru transparență totală, contractul poate fi consultat oricând înainte de semnare.\n\n👉 Previzualizare Contract Intermediere Proprietar\n\n📌 În platformă, acest link va fi accesibil printr-un buton de previzualizare.',
            type: 'contract-preview',
            contractUrl:
              'https://drive.google.com/file/d/1KnJAwr0OT26MU3HmrRXFPPMa3n1ZRS4x/view?usp=drive_link',
          },
          {
            id: 'refuz-contract',
            title: 'Ce se întâmplă dacă proprietarul refuză contractul',
            emoji: '🛑',
            number: '8️⃣',
            content:
              'Dacă proprietarul:\n\n• refuză semnarea contractului\n• nu dorește să colaboreze în aceste condiții\n\n➡️ procesul se oprește.\n\n👉 **Nu se fac:**\n\n• vizionări\n• promovări\n• excepții\n\n📌 Profesionalismul începe cu reguli clare.',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Refuz contract',
            quote: 'Profesionalismul începe cu reguli clare.',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '9️⃣',
            content:
              '❌ Vizionări fără contract\n❌ „Lasă că semnăm după"\n❌ Date incomplete în contract\n❌ Explicații complicate sau defensive',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Greșeli de evitat',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să prezinți și să semnezi contractul cu proprietarul într-un mod profesionist și fără tensiuni.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '🔟',
            content:
              'Contractul cu proprietarul:\n\n• nu este o barieră\n• este o protecție\n• este baza colaborării\n\n**Reține:**\n\n• cost 0 pe muncă\n• comision doar pe rezultat\n• control total la proprietar\n\n💬 Un agent profesionist nu negociază regulile de bază. Le explică și merge mai departe.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes contract proprietar',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'contract-chirias',
        title: 'Contract Chiriaș',
        duration: '26 min',
        description: 'Întocmirea contractului cu chiriașul',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'În închiriere, una dintre cele mai frecvente greșeli este:\n👉 vizionarea fără contract cu chiriașul.\n\n**Contractul cu chiriașul:**\n\n• NU este o formalitate\n• NU este un cost ascuns\n• ESTE documentul care clarifică regulile colaborării\n\n📌 Fără contract semnat cu chiriașul → nu există vizionare.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract chiriaș',
            quote: 'Fără contract semnat cu chiriașul → nu există vizionare.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• ce este contractul de intermediere cu chiriașul\n• de ce este obligatoriu\n• când se semnează\n• ce clarifică pentru chiriaș și agent\n• cum explici contractul simplu și natural\n• ce faci dacă chiriașul refuză semnarea',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'ce-este-contractul',
            title: 'Ce este Contractul Chiriaș',
            emoji: '📄',
            number: '1️⃣',
            content:
              'Contractul de intermediere cu chiriașul este:\n\n• acordul dintre agenție și chiriaș\n• documentul care stabilește:\n  - serviciile oferite\n  - comisionarea\n  - momentul plății\n  - regulile vizionărilor\n\n📌 Contractul NU obligă chiriașul să închirieze.\n📌 Contractul NU generează costuri fără rezultat.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Contract intermediere chiriaș',
            type: 'text',
          },
          {
            id: 'de-ce-obligatoriu',
            title: 'De ce este obligatoriu contractul cu chiriașul',
            emoji: '🛡️',
            number: '2️⃣',
            content:
              'Contractul:\n\n• protejează munca agentului\n• elimină neînțelegerile legate de comision\n• creează un cadru profesionist\n• filtrează chiriașii neserioși\n\n👉 **Vizionările fără contract duc la:**\n\n• pierdere de timp\n• refuzuri la final\n• conflicte inutile',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Protecție contract',
            type: 'text',
          },
          {
            id: 'cand-se-semneaza',
            title: 'Când se semnează Contractul Chiriaș',
            emoji: '✍️',
            number: '3️⃣',
            content:
              'Contractul se semnează:\n\n• înainte de orice vizionare\n• fizic sau digital\n\n📌 **Regula este fermă:**\n\n❌ fără contract semnat → ❌ fără vizionare\n\n**Nu există:**\n\n• „vedem după"\n• „semnăm la fața locului"\n• excepții',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Semnare contract',
            type: 'text',
          },
          {
            id: 'ce-clarifica',
            title: 'Ce clarifică Contractul Chiriașului',
            emoji: '📋',
            number: '4️⃣',
            content:
              'Contractul explică clar:\n\n• comisionul agenției\n• momentul în care se plătește\n• faptul că plata apare doar dacă se închiriază\n• rolul agentului în proces\n• obligațiile ambelor părți\n\n📌 **Chiriașul știe exact:**\n\n• cât plătește\n• când plătește\n• pentru ce plătește',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Clarificări contract',
            type: 'text',
          },
          {
            id: 'cum-explici',
            title: 'Cum explici contractul chiriașului (uman, simplu)',
            emoji: '💬',
            number: '5️⃣',
            content:
              '**Exemplu de explicație:**\n\n"Contractul nu vă obligă să închiriezi nimic. Este doar documentul prin care stabilim clar:\n\n– că vă asist pe tot procesul\n– că nu aveți costuri fără rezultat\n– și că plata apare doar dacă se închiriază."\n\n📌 Fără presiune. Fără limbaj juridic.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Explicație contract',
            type: 'text',
          },
          {
            id: 'previzualizare-contract',
            title: 'Previzualizare Contract Chiriaș',
            emoji: '📄',
            number: '6️⃣',
            content:
              'Pentru transparență, chiriașul poate consulta contractul înainte de semnare.\n\n👉 Previzualizare Contract Intermediere Chiriaș\n\n📌 În platformă, acest link va fi accesibil printr-un buton de previzualizare.',
            type: 'contract-preview',
            contractUrl:
              'https://drive.google.com/file/d/1X8kvJ2J6474SoHQeB4vswtEoyMMzEqUS/view?usp=drive_link',
          },
          {
            id: 'refuz-semnare',
            title: 'Ce faci dacă chiriașul refuză semnarea',
            emoji: '🛑',
            number: '7️⃣',
            content:
              'Dacă chiriașul:\n\n• refuză semnarea contractului\n• nu acceptă regulile colaborării\n\n➡️ procesul se oprește.\n\n👉 **Nu se fac:**\n\n• vizionări\n• excepții\n• promisiuni verbale\n\n📌 Profesionalismul începe cu reguli clare.',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Refuz semnare',
            quote: 'Profesionalismul începe cu reguli clare.',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '8️⃣',
            content:
              '❌ Vizionări fără contract\n❌ Amânarea semnării\n❌ Explicații complicate sau defensive\n❌ Negocierea regulilor de bază',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Greșeli de evitat',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să prezinți și să semnezi contractul cu chiriașul într-un mod profesionist și natural.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Contractul cu chiriașul:\n\n• nu este o piedică\n• este un filtru\n• este o protecție pentru toată lumea\n\n**Reține:**\n\n• fără contract → fără vizionare\n• fără rezultat → fără cost\n• reguli clare → proces simplu\n\n💬 Un agent profesionist nu evită contractul. Îl explică, îl semnează și merge mai departe.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes contract chiriaș',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
  {
    id: 'vizionare',
    title: 'Modul 5 — Vizionare',
    icon: 'eye',
    description: 'Organizarea și desfășurarea vizionărilor de proprietăți',
    lessons: [
      {
        id: 'organizare-indexare-vizionari',
        title: 'Organizare și Indexare Vizionări',
        duration: '24 min',
        description: 'Planificarea și documentarea vizionărilor',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'Vizionarea nu este o plimbare printr-un apartament. Este un moment critic în procesul de închiriere.\n\n**O vizionare prost organizată:**\n\n• consumă timp\n• generează frustrări\n• scade șansele de închidere\n\n**O vizionare bine organizată:**\n\n• filtrează decizia\n• accelerează închirierea\n• menține controlul agentului',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Vizionare profesională',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• când se poate organiza o vizionare\n• ce acțiuni sunt obligatorii înainte de vizionare\n• cum se desfășoară corect vizionarea\n• ce se face imediat după vizionare\n• cum se indexează corect vizionările în CRM și comunitatea CLC',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'regula-zero',
            title: 'Regula zero a vizionării',
            emoji: '📌',
            number: '1️⃣',
            content:
              '📌 **O vizionare poate avea loc DOAR dacă:**\n\n• contractul cu proprietarul este semnat\n• contractul cu chiriașul este semnat\n\n❌ Dacă una dintre părți refuză semnarea → vizionarea se anulează.\n\n**Semnarea poate fi:**\n\n• fizică, înainte de vizionare\n• digitală, trimisă și semnată înainte de întâlnire',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contracte obligatorii',
            quote:
              'Dacă una dintre părți refuză semnarea → vizionarea se anulează.',
            type: 'text',
          },
          {
            id: 'actiuni-inainte',
            title: 'Acțiuni obligatorii ÎNAINTE de vizionare',
            emoji: '✅',
            number: '2️⃣',
            content:
              '**🔹 Confirmarea documentelor**\n\nAgentul verifică:\n\n• contract proprietar – semnat\n• contract chiriaș – semnat\n• datele completate corect\n\n**🔹 Confirmarea părților**\n\n• confirmi ora cu proprietarul\n• confirmi ora cu chiriașul\n• confirmi adresa exactă\n\n📌 Fără confirmare → risc de vizionare ratată.\n\n**🔹 Pregătirea vizionării**\n\nAgentul trebuie să știe:\n\n• detalii reale despre imobil\n• condițiile proprietarului\n• ce tip de chiriaș este acceptat',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Pregătire vizionare',
            type: 'text',
          },
          {
            id: 'desfasurare-vizionare',
            title: 'Desfășurarea vizionării (ÎN TIMPUL vizionării)',
            emoji: '🏠',
            number: '3️⃣',
            content:
              'În timpul vizionării:\n\n• agentul conduce discuția\n• prezintă imobilul clar\n• răspunde la întrebări\n• observă reacțiile chiriașului\n\n📌 Agentul NU promite în numele proprietarului.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Desfășurare vizionare',
            quote: 'Agentul NU promite în numele proprietarului.',
            type: 'text',
          },
          {
            id: 'actiuni-dupa',
            title: 'Acțiuni IMEDIAT DUPĂ vizionare',
            emoji: '📝',
            number: '4️⃣',
            content:
              'După vizionare:\n\n• ceri feedback de la chiriaș\n• notezi interesul real\n• verifici dacă există intenție de închiriere\n\n📌 Feedback-ul trebuie obținut pe loc sau imediat.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Feedback după vizionare',
            type: 'text',
          },
          {
            id: 'indexare-crm',
            title: 'Indexarea vizionării în CRM (OBLIGATORIU)',
            emoji: '💼',
            number: '5️⃣',
            content:
              'Fiecare vizionare se indexează:\n\n• în CRM\n• cu dată, oră, imobil, părți implicate\n• cu status:\n  - interesat\n  - neinteresat\n  - în analiză\n\n📌 Ce nu este indexat NU există.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Indexare CRM',
            quote: 'Ce nu este indexat NU există.',
            type: 'text',
          },
          {
            id: 'indexare-grup',
            title:
              'Indexarea în Grupul de Vizionări – Comunitatea Cautlocuinta.ro',
            emoji: '👥',
            number: '6️⃣',
            content:
              'După CRM:\n\n• vizionarea se adaugă și în Grupul de Vizionări\n• agenții au acces la status\n• se evită suprapuneri și confuzii\n\n📌 Comunitatea este instrument de coordonare, nu chat informal.',
            image:
              'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1200&h=600&fit=crop',
            imageAlt: 'Comunitate CLC',
            type: 'text',
          },
          {
            id: 'decizie-dupa-vizionare',
            title: 'Decizia după vizionare',
            emoji: '🎯',
            number: '7️⃣',
            content:
              'În funcție de feedback:\n\n• se merge spre contract P–C\n• se recalifică chiriașul\n• se continuă matching-ul\n\n📌 Vizionarea trebuie să ducă la o decizie, nu la incertitudine.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Decizie după vizionare',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '8️⃣',
            content:
              '❌ Vizionări fără contracte\n❌ Lipsa confirmărilor\n❌ Feedback lipsă\n❌ Vizionări neindexate\n❌ Promisiuni neasumate',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Greșeli de evitat',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să organizezi și să documentezi corect vizionările.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Vizionarea este:\n\n• un pas intermediar\n• nu un scop în sine\n\n**Reține:**\n\n• fără contract → fără vizionare\n• fără organizare → pierdere de timp\n• fără indexare → haos\n\n💬 Un agent profesionist organizează, documentează și decide. Nu improvizează.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes vizionare',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'recalificare-cereri',
        title: 'Recalificarea Chiriașului',
        duration: '16 min',
        description: 'Re-evaluarea și actualizarea profilului cererilor',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'După una sau mai multe vizionări, apare momentul critic:\n👉 chiriașul nu închiriază.\n\nAici se pierd cei mai mulți agenți:\n\n• continuă să lucreze aceiași chiriași\n• fără ajustări\n• fără decizii clare\n\n📌 Recalificarea chiriașului NU este un eșec. Este un act de control.',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Recalificare chiriaș',
            quote:
              'Recalificarea chiriașului NU este un eșec. Este un act de control.',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție vei ști:\n\n• când se face recalificarea\n• ce întrebări trebuie puse după vizionări\n• cum decizi dacă un chiriaș mai merită lucrat\n• cum identifici cererile necoerente\n• cum se indexează corect recalificarea în CRM',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'cand-se-face',
            title: 'Când se face Recalificarea Chiriașului',
            emoji: '⏰',
            number: '1️⃣',
            content:
              'Recalificarea se face:\n\n• după fiecare vizionare fără rezultat\n• după 2–3 vizionări nereușite\n• când feedback-ul este constant negativ\n\n📌 Nu continui automat. Reevaluezi.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Momentul recalificării',
            type: 'text',
          },
          {
            id: 'scopul-recalificarii',
            title: 'Scopul recalificării',
            emoji: '🎯',
            number: '2️⃣',
            content:
              'Recalificarea urmărește:\n\n• ajustarea cererii la piață\n• eliminarea blocajelor\n• decizia clară: continuăm sau nu\n\n👉 Fără recalificare, agentul muncește în gol.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Scop recalificare',
            quote: 'Fără recalificare, agentul muncește în gol.',
            type: 'text',
          },
          {
            id: 'intrebari-cheie',
            title: 'Întrebările-cheie de recalificare',
            emoji: '❓',
            number: '3️⃣',
            content:
              'După vizionări, întrebi clar:\n\n**🔹 Ce nu a fost potrivit?**\n\n• prețul\n• zona\n• compartimentarea\n• starea imobilului\n\n**🔹 Ce este negociabil?**\n\n• zona\n• bugetul\n• tipul imobilului\n\n**🔹 Ce rămâne obligatoriu?**\n\n• termenul de mutare\n• număr persoane\n• alte condiții fixe\n\n📌 Răspunsurile trebuie să fie concrete, nu vagi.',
            image:
              'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop',
            imageAlt: 'Întrebări recalificare',
            type: 'text',
          },
          {
            id: 'coerenta-cererii',
            title: 'Coerența cererii cu piața',
            emoji: '📊',
            number: '4️⃣',
            content:
              'Agentul trebuie să decidă dacă:\n\n• cererea este realistă\n• sau este o așteptare nerealistă\n\n**⚠️ Semne de cerere necoerentă:**\n\n• buget prea mic pentru zonă\n• refuz constant al variantelor corecte\n• lipsă flexibilitate totală\n\n👉 Aici agentul trebuie să spună adevărul, nu ce vrea chiriașul să audă.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Coerență cerere',
            type: 'text',
          },
          {
            id: 'decizia-agentului',
            title: 'Decizia agentului după recalificare',
            emoji: '✅',
            number: '5️⃣',
            content:
              'După analiză:\n\n**✅ Continuăm lucrul**\n\n• cererea este ajustată\n• există flexibilitate\n• chiriașul înțelege piața\n\n**⚠️ Punem pe pauză**\n\n• timing neclar\n• buget insuficient temporar\n\n**❌ Oprim colaborarea**\n\n• cerere nerealistă\n• refuz de ajustare\n• lipsă decizie\n\n📌 A opri un chiriaș este uneori cea mai bună decizie.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Decizie agent',
            quote: 'A opri un chiriaș este uneori cea mai bună decizie.',
            type: 'text',
          },
          {
            id: 'regula-10-chirasi',
            title: 'Regula celor 10 chiriași activi',
            emoji: '🔢',
            number: '6️⃣',
            content:
              'Un agent:\n\n• NU poate lucra eficient mai mult de 10 chiriași activi\n• trebuie să îi prioritizeze\n\n**Recalificarea ajută la:**\n\n• eliminarea celor neproductivi\n• focus pe cereri reale\n\n📌 Dacă apare un chiriaș nou:\n👉 înlocuiești unul slab din listă.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Prioritizare chiriași',
            type: 'text',
          },
          {
            id: 'indexare-crm',
            title: 'Indexarea recalificării în CRM (OBLIGATORIU)',
            emoji: '💼',
            number: '7️⃣',
            content:
              'După recalificare:\n\n• actualizezi cererea în CRM\n• notezi:\n  - modificările\n  - decizia\n  - statusul nou\n\n📌 Ce nu este indexat NU există.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Indexare CRM',
            quote: 'Ce nu este indexat NU există.',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '8️⃣',
            content:
              '❌ „Hai să mai vedem" la nesfârșit\n❌ Vizionări repetitive fără ajustări\n❌ Lipsa deciziei\n❌ Lucrul cu chiriași fără potențial',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Greșeli de evitat',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește explicația video',
            emoji: '🎬',
            content:
              'Avatarul AI îți arată pas cu pas cum să recalifici corect chiriașii și să iei decizii inteligente.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Recalificarea chiriașului:\n\n• salvează timp\n• crește randamentul\n• reduce frustrarea\n\n📌 **Agenții slabi muncesc mult**\n📌 **Agenții buni muncesc inteligent**\n\n💬 Un agent profesionist nu se atașează de cereri. Se atașează de rezultate.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes recalificare',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
  {
    id: 'inchidere-tranzactie',
    title: 'Modul 6 — Închidere Tranzacție & Contabilitate',
    icon: 'check-circle',
    description:
      'Finalizarea tranzacțiilor și gestionarea aspectelor financiare',
    lessons: [
      {
        id: 'contract-inchiriere-p-c',
        title: 'Contract Închiriere P–C',
        duration: '30 min',
        description: 'Contractul final între proprietar și chiriaș',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'Închiderea unei tranzacții de închiriere NU înseamnă doar „client interesat". O tranzacție este considerată închisă corect doar în momentul în care:\n\n👉 între Proprietar și Chiriaș există un contract de închiriere semnat\n\nRolul agentului nu este să fie parte în contract, ci să asiste procesul, să evite blocaje și să se asigure că lucrurile sunt făcute corect, legal și clar pentru ambele părți.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Contract de închiriere și chei',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• ce este contractul de închiriere P–C\n• când și cum se semnează\n• care este rolul agentului în proces\n• ce se întâmplă dacă semnarea se amână\n• când devin Proprietarul și Chiriașul eligibili pentru facturare\n• cum previi pierderea unei tranzacții după vizionare',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție',
            type: 'text',
          },
          {
            id: 'ce-este',
            title: 'Ce este contractul de închiriere P–C',
            emoji: '📄',
            number: '1️⃣',
            content:
              'Contractul de închiriere este acordul legal dintre:\n\n• Proprietar (locator)\n• Chiriaș (locatar)\n\nPrin care:\n\n• se stabilește chiria\n• durata\n• drepturile și obligațiile fiecărei părți\n\n📌 Agenția NU este parte în acest contract.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract de închiriere',
            type: 'text',
          },
          {
            id: 'rolul-agentului',
            title: 'Rolul agentului în contractul P–C',
            emoji: '🤝',
            number: '2️⃣',
            content:
              'Agentul:\n\n• NU semnează contractul\n• NU impune un model obligatoriu\n• NU decide condițiile finale\n\nAgentul ARE rolul de a:\n\n• asista furnizarea corectă a datelor\n• verifica completarea corectă (nume, CNP, adresă exactă imobil)\n• asista părțile la semnare\n• preveni neclarități sau conflicte ulterioare\n\n👉 Agentul este facilitator, nu parte contractuală.',
            image:
              'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200&h=600&fit=crop',
            imageAlt: 'Agent asistând semnarea',
            quote: 'Agentul este facilitator, nu parte contractuală.',
            type: 'text',
          },
          {
            id: 'modelul-contract',
            title: 'Modelul de contract propus de agenție',
            emoji: '📋',
            number: '3️⃣',
            content:
              'Cautlocuinta.ro propune un model standard de contract între Proprietar și Chiriaș, pentru a:\n\n• simplifica procesul\n• evita lipsuri sau erori\n• accelera semnarea\n\n📌 Important:\n\n• NU este obligatoriu\n• Proprietarul sau Chiriașul pot veni cu alt contract\n• Agentul respectă decizia părților',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Model contract',
            type: 'text',
          },
          {
            id: 'previzualizare-contract',
            title: 'Previzualizare Contract Închiriere P–C',
            emoji: '📄',
            number: '3️⃣.1',
            content:
              'Mai jos poți vizualiza modelul oficial de contract de închiriere între Proprietar și Chiriaș folosit de agenție.\n\nAcest contract conține toate clauzele necesare pentru o închiriere legală și sigură.',
            type: 'contract-preview',
            contractUrl:
              'https://docs.google.com/document/d/18fNWrniRG6_xy9yCp5K7Cqy1oKQOxIjo32vN55J8U90/edit?usp=drive_link',
          },
          {
            id: 'cand-se-semneaza',
            title: 'Când se semnează contractul de închiriere',
            emoji: '✍️',
            number: '4️⃣',
            content:
              'Contractul P–C se poate semna:\n\n• imediat după vizionare\n• sau la o dată ulterioară stabilită de comun acord\n\nSemnarea poate fi:\n\n✍️ fizică\n💻 digitală (semnătură electronică)\n\n👉 Ambele variante sunt acceptate.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Semnare contract',
            type: 'text',
          },
          {
            id: 'semnare-intarziata',
            title: 'Situația semnării întârziate (peste 7 zile)',
            emoji: '⏰',
            number: '5️⃣',
            content:
              'Dacă semnarea contractului este programată la mai mult de 7 zile, agentul trebuie să recomande:\n\n🔒 Rezervare între Proprietar și Chiriaș\n\nRezerva rea:\n\n• este un acord temporar\n• include un angajament financiar\n• demonstrează seriozitatea chiriașului\n\n📌 Valoare uzuală:\n\n• 20% din valoarea unei luni de chirie\n• suma se scade ulterior din:\n  - chiria în avans\n  - sau garanție\n\n👉 Rezervarea reduce riscul de răzgândire.',
            image:
              'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&h=600&fit=crop',
            imageAlt: 'Rezervare și calendar',
            type: 'text',
          },
          {
            id: 'eligibilitate-facturare',
            title: 'Eligibilitatea pentru facturare',
            emoji: '💰',
            number: '6️⃣',
            content:
              'După semnarea contractului de închiriere:\n\n• Proprietarul devine eligibil pentru factura de comision\n• Chiriașul devine eligibil pentru factura de comision (dacă este cazul)\n\n📌 Facturarea se face:\n\n• DOAR după existența contractului P–C\n• conform condițiilor agreate în contractele de intermediere',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Facturare și plăți',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '7️⃣',
            content:
              '❌ Considerarea tranzacției „închise" fără contract\n❌ Lăsarea părților să se descurce singure\n❌ Amânări fără rezervare\n❌ Neverificarea datelor din contract\n❌ Presupunerea că „se rezolvă între ei"\n\n👉 Aceste greșeli duc la pierderi de timp și bani.',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Atenționare greșeli',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește prezentarea video',
            content:
              'În acest video, avatarul AI îți explică pas cu pas cum se gestionează contractul de închiriere P–C și cum să asiguri o finalizare corectă.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Un agent profesionist știe că:\n\n• interesul NU este suficient\n• vizionarea NU este finalul\n• contractul semnat este cheia\n\nReține:\n\n• agenția facilitează\n• părțile decid\n• agentul asigură corectitudinea procesului\n\n👉 O tranzacție de închiriere bine închisă înseamnă:\n\n• claritate\n• siguranță\n• încasare corectă a comisionului',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în închiderea tranzacției',
            type: 'conclusion',
          },
        ],
      },
      {
        id: 'facturare-incasare',
        title: 'Facturare și Încasare',
        duration: '22 min',
        description: 'Procesul de facturare și colectare a plăților',
        sections: [
          {
            id: 'introducere',
            title: 'Introducere',
            emoji: '🔹',
            content:
              'O tranzacție de închiriere NU este finalizată complet în momentul în care:\n\n• proprietarul și chiriașul sunt de acord\n• sau când contractul este semnat\n\n👉 Tranzacția este finalizată doar după facturare și încasare.\n\nRolul agentului este esențial:\n\n• să se asigure că facturarea este făcută corect\n• să furnizeze toate datele necesare\n• să evite blocaje, întârzieri sau greșeli',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Facturare și documentație',
            type: 'text',
          },
          {
            id: 'obiectiv',
            title: 'Obiectivul lecției',
            emoji: '🎯',
            number: '0️⃣',
            content:
              'După această lecție, vei ști:\n\n• când se emite factura\n• ce documente sunt obligatorii\n• ce date trebuie colectate\n• care sunt metodele de solicitare a facturii\n• cum se face corect indexarea tranzacției în platforma CLC',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Obiective lecție facturare',
            type: 'text',
          },
          {
            id: 'regula-baza',
            title: 'Regula de bază: când se emite factura',
            emoji: '📌',
            number: '1️⃣',
            content:
              'Facturile se emit DOAR după semnarea contractului de închiriere între Proprietar și Chiriaș.\n\nNu înainte. Nu pe promisiuni. Nu pe „urmează să semnăm".\n\n👉 Contractul P–C este condiția zero pentru facturare.',
            image:
              'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&h=600&fit=crop',
            imageAlt: 'Contract semnat',
            quote: 'Contractul P–C este condiția zero pentru facturare.',
            type: 'text',
          },
          {
            id: 'cine-poate-fi-facturat',
            title: 'Cine poate fi facturat',
            emoji: '👥',
            number: '2️⃣',
            content:
              'În funcție de tranzacție, factura poate fi emisă către:\n\n• Proprietar\n• Chiriaș\n• Ambele părți\n\nConform:\n\n• contractelor de intermediere semnate\n• condițiilor agreate anterior\n\n📌 Agentul NU decide singur cui se facturează, ci respectă structura tranzacției.',
            image:
              'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=1200&h=600&fit=crop',
            imageAlt: 'Părțile contractuale',
            type: 'text',
          },
          {
            id: 'informatii-necesare',
            title: 'Ce informații sunt necesare pentru facturare',
            emoji: '📋',
            number: '3️⃣',
            content:
              'Pentru emiterea unei facturi, sunt necesare date complete și corecte, exact ca în platformă:\n\n📄 **Date tranzacție:**\n• Tip tranzacție: Închiriere\n• Valoare tranzacție (chirie lunară)\n• Comision aplicat (%)\n• TVA (inclus / +TVA)\n• Data tranzacției\n• Agent responsabil (Closer CLC)\n\n🏠 **Date imobil:**\n• Tip imobil\n• Adresă completă (exact ca în contract)\n\n👤 **Date client – Proprietar:**\n• Persoană fizică / juridică\n• Nume complet\n• Telefon\n• Email (dacă există)\n• CNP / CUI\n• Serie CI\n• Adresă domiciliu / sediu\n\n👤 **Date client – Chiriaș:**\n• Persoană fizică / juridică\n• Nume complet\n• Telefon\n• Email (dacă există)\n• CNP / CUI\n• Serie CI\n• Adresă domiciliu / sediu\n\n📎 **Documente obligatorii:**\n• Contract de intermediere Proprietar (semnat)\n• Contract de intermediere Chiriaș (semnat)\n• Contract de închiriere P–C (semnat)\n\n👉 Fără aceste documente, factura NU se emite.',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Documentație completă',
            type: 'text',
          },
          {
            id: 'modalitati-solicitare',
            title: 'Modalități de solicitare a facturii',
            emoji: '📧',
            number: '4️⃣',
            content:
              'Există două variante corecte:\n\n📧 **Varianta 1 – prin email:**\n• Datele se trimit către contabilitatea CLC\n• Contabilitatea emite factura\n• Factura este transmisă către client\n\n📌 Această variantă se folosește doar dacă nu este disponibilă platforma.\n\n💻 **Varianta 2 – prin platforma CLC (recomandat):**\n\nAgentul:\n• completează formularul „Adaugă cerere de factură"\n• încarcă documentele semnate\n• aplică calculul de comision\n• indexează tranzacția\n\n👉 Platforma CLC centralizează tot procesul și reduce erorile.',
            image:
              'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop',
            imageAlt: 'Platformă digitală',
            type: 'text',
          },
          {
            id: 'calcul-comision',
            title: 'Calculul comisionului și TVA',
            emoji: '🧮',
            number: '5️⃣',
            content:
              'În platformă:\n\n• se introduce valoarea tranzacției\n• se selectează părțile comisionate\n• se aplică procentul de comision\n• se selectează opțiunea TVA\n\n📌 Agentul trebuie să verifice:\n\n• corectitudinea valorilor\n• suma totală de facturat\n• suma eligibilă pentru comisionare (fără TVA)',
            image:
              'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&h=600&fit=crop',
            imageAlt: 'Calculator și verificare',
            type: 'text',
          },
          {
            id: 'incasare-comision',
            title: 'Încasarea comisionului',
            emoji: '💰',
            number: '6️⃣',
            content:
              'După emiterea facturii:\n\n• clientul achită factura conform termenului\n• încasarea este înregistrată\n• tranzacția este considerată complet finalizată\n\n👉 Agentul trebuie să urmărească:\n\n• dacă factura a fost transmisă\n• dacă a fost încasată\n• dacă statusul tranzacției este actualizat',
            image:
              'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&h=600&fit=crop',
            imageAlt: 'Încasare și finalizare',
            type: 'text',
          },
          {
            id: 'greseli-evitat',
            title: 'Greșeli frecvente de evitat',
            emoji: '⚠️',
            number: '7️⃣',
            content:
              '❌ Trimiterea facturii fără contract P–C\n❌ Date incomplete sau greșite\n❌ Neîncărcarea documentelor\n❌ Lipsa indexării în platformă\n❌ Presupunerea că „se ocupă altcineva"\n\n👉 Orice greșeală aici duce la întârzierea încasării.',
            image:
              'https://images.unsplash.com/photo-1621252179027-94459d278660?w=1200&h=600&fit=crop',
            imageAlt: 'Atenționare greșeli',
            type: 'text',
          },
          {
            id: 'video',
            title: 'Urmărește prezentarea video',
            content:
              'În acest video, avatarul AI îți explică pas cu pas procesul complet de facturare și încasare.',
            type: 'video',
          },
          {
            id: 'concluzie',
            title: 'Concluzie',
            emoji: '🧩',
            number: '9️⃣',
            content:
              'Facturarea și încasarea sunt:\n\n• ultima etapă\n• dar una critică\n\nReține:\n\n• contract semnat → factură\n• date corecte → proces rapid\n• indexare → control și transparență\n\n💬 Un agent bun închide tranzacții. Un agent profesionist le închide corect și complet.',
            image:
              'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop',
            imageAlt: 'Succes în închiderea completă',
            type: 'conclusion',
          },
        ],
      },
    ],
  },
]

// Structured course data that maps modules to courses
export const coursesStructure: Course[] = [
  {
    id: 'concept-clc',
    title: 'Training 1 — Concept CLC',
    description:
      'Înțelegerea conceptului Cautlocuință.ro, a structurii agenției și a ecosistemului profesional.',
    status: 'active',
    modules: conceptClcModules,
  },
  {
    id: 'proceduri-inchiriere',
    title: 'Training 2 — Proceduri și Tranzacții Închiriere',
    description:
      'Procedurile complete pentru activitatea de închiriere: prospectare, clienți, matching, contracte și închidere.',
    status: 'active',
    modules: courseData,
  },
]

// Helper functions for accessing course data

// Legacy functions (for backward compatibility with non-course routes)
export function getModuleById(moduleId: string): Module | undefined {
  return courseData.find((module) => module.id === moduleId)
}

export function getLessonById(
  moduleId: string,
  lessonId: string,
): Lesson | undefined {
  const module = getModuleById(moduleId)
  return module?.lessons.find((lesson) => lesson.id === lessonId)
}

export function getNextLesson(
  moduleId: string,
  lessonId: string,
): { module: Module; lesson: Lesson } | null {
  const module = getModuleById(moduleId)
  if (!module) return null

  const currentLessonIndex = module.lessons.findIndex((l) => l.id === lessonId)
  if (currentLessonIndex === -1) return null

  // Check if there's a next lesson in the current module
  if (currentLessonIndex < module.lessons.length - 1) {
    return {
      module,
      lesson: module.lessons[currentLessonIndex + 1],
    }
  }

  // Check if there's a next module
  const currentModuleIndex = courseData.findIndex((m) => m.id === moduleId)
  if (currentModuleIndex < courseData.length - 1) {
    const nextModule = courseData[currentModuleIndex + 1]
    return {
      module: nextModule,
      lesson: nextModule.lessons[0],
    }
  }

  return null
}

export function getPreviousLesson(
  moduleId: string,
  lessonId: string,
): { module: Module; lesson: Lesson } | null {
  const module = getModuleById(moduleId)
  if (!module) return null

  const currentLessonIndex = module.lessons.findIndex((l) => l.id === lessonId)
  if (currentLessonIndex === -1) return null

  // Check if there's a previous lesson in the current module
  if (currentLessonIndex > 0) {
    return {
      module,
      lesson: module.lessons[currentLessonIndex - 1],
    }
  }

  // Check if there's a previous module
  const currentModuleIndex = courseData.findIndex((m) => m.id === moduleId)
  if (currentModuleIndex > 0) {
    const previousModule = courseData[currentModuleIndex - 1]
    return {
      module: previousModule,
      lesson: previousModule.lessons[previousModule.lessons.length - 1],
    }
  }

  return null
}

// Course-specific functions (for new course-based routes)
export function getModuleByCourseAndId(
  courseId: string,
  moduleId: string,
): Module | undefined {
  const course = coursesStructure.find((c) => c.id === courseId)
  return course?.modules.find((module) => module.id === moduleId)
}

export function getLessonByCourseModuleAndId(
  courseId: string,
  moduleId: string,
  lessonId: string,
): Lesson | undefined {
  const module = getModuleByCourseAndId(courseId, moduleId)
  return module?.lessons.find((lesson) => lesson.id === lessonId)
}

export function getNextLessonInCourse(
  courseId: string,
  moduleId: string,
  lessonId: string,
): { courseId: string; module: Module; lesson: Lesson } | null {
  const course = coursesStructure.find((c) => c.id === courseId)
  if (!course) return null

  const module = course.modules.find((m) => m.id === moduleId)
  if (!module) return null

  const currentLessonIndex = module.lessons.findIndex((l) => l.id === lessonId)
  if (currentLessonIndex === -1) return null

  // Check if there's a next lesson in the current module
  if (currentLessonIndex < module.lessons.length - 1) {
    return {
      courseId,
      module,
      lesson: module.lessons[currentLessonIndex + 1],
    }
  }

  // Check if there's a next module in the same course
  const currentModuleIndex = course.modules.findIndex((m) => m.id === moduleId)
  if (currentModuleIndex < course.modules.length - 1) {
    const nextModule = course.modules[currentModuleIndex + 1]
    return {
      courseId,
      module: nextModule,
      lesson: nextModule.lessons[0],
    }
  }

  return null
}

export function getPreviousLessonInCourse(
  courseId: string,
  moduleId: string,
  lessonId: string,
): { courseId: string; module: Module; lesson: Lesson } | null {
  const course = coursesStructure.find((c) => c.id === courseId)
  if (!course) return null

  const module = course.modules.find((m) => m.id === moduleId)
  if (!module) return null

  const currentLessonIndex = module.lessons.findIndex((l) => l.id === lessonId)
  if (currentLessonIndex === -1) return null

  // Check if there's a previous lesson in the current module
  if (currentLessonIndex > 0) {
    return {
      courseId,
      module,
      lesson: module.lessons[currentLessonIndex - 1],
    }
  }

  // Check if there's a previous module in the same course
  const currentModuleIndex = course.modules.findIndex((m) => m.id === moduleId)
  if (currentModuleIndex > 0) {
    const previousModule = course.modules[currentModuleIndex - 1]
    return {
      courseId,
      module: previousModule,
      lesson: previousModule.lessons[previousModule.lessons.length - 1],
    }
  }

  return null
}
